import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { RelatedGuidesBlock } from "@/components/RelatedGuidesBlock";
import { Faq } from "@/components/Faq";
import { jsonLd, SITE_URL, faqSchema, breadcrumbSchema, absUrl } from "@/lib/seo";
import { track } from "@/lib/analytics";
import { internalPath } from "@/lib/paths";
import { withBookingUtm } from "@/lib/booking";
import { PRICE } from "@/data/pricing";

const SLUG = "barberare-oppet-sondag-stockholm";
const CANONICAL = `${SITE_URL}/${SLUG}/`;
const TITLE = "Barberare öppet söndagar i Stockholm: 4 salonger | Tailor's Barber";
const DESCRIPTION =
  "Barberare öppettider söndagar: Tailor's Barber har öppet söndagar på fyra salonger i Stockholm. Herrklippning 450 kr, drop-in i mån av plats. Boka online.";

const SALONS = [
  {
    name: "Åhléns City",
    subtitle: "Stockholm City",
    address: "Klarabergsgatan 50, Stockholm",
    sundayHours: "11:00 till 19:00",
    url: "/barberare-stockholm-ahlens-city",
    bokaDirektUrl: "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
    note: "Vår city-salong inne i Åhléns: nås enkelt med tunnelbana och buss.",
  },
  {
    name: "Täby Centrum",
    subtitle: "Flaggskeppssalongen",
    address: "Biblioteksgången 1, Täby",
    sundayHours: "10:00 till 18:00",
    url: "/barberare-stockholm-taby",
    bokaDirektUrl: "https://www.bokadirekt.se/places/tailors-barber-taby-centrum-37677",
    note: "Vår flaggskeppssalong i Westfield Täby Centrum: stort parkeringsgarage i centret.",
  },
  {
    name: "Arninge",
    subtitle: "ICA Maxi Arninge",
    address: "Kundvägen 1D, Täby",
    sundayHours: "10:00 till 18:00",
    url: "/barberare-stockholm-arninge",
    bokaDirektUrl: "https://www.bokadirekt.se/places/tailors-barber-arninge-ica-maxi-huset--61598",
    note: "Nära IKEA och Bauhaus: gratis parkering och lättillgängligt.",
  },
  {
    name: "Åkersberga",
    subtitle: "ICA Maxi Åkersberga",
    address: "Långhundravägen 9, Åkersberga",
    sundayHours: "10:00 till 18:00",
    url: "/barberare-stockholm-akersberga",
    bokaDirektUrl: "https://www.bokadirekt.se/places/tailors-barber-akersberga-ica-maxi-huset--37682",
    note: "Österåkers enda specialiserade barbershop: gratis parkering utanför ICA Maxi.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Vilka barberare i Stockholm har öppet på söndagar?",
    a: "Tailor's Barber har öppet på söndagar i alla fyra salonger: Åhléns City (11 till 19), Täby Centrum (10 till 18), Arninge (10 till 18) och Åkersberga (10 till 18).",
  },
  {
    q: "Kan man drop-ina på söndagar?",
    a: "Ja, drop-in är välkommen i mån av tid på alla salonger. Söndagar kan vara populära. Boka gärna online via BokaDirekt för att garantera din plats.",
  },
  {
    q: "Kostar det mer att klippa sig på söndagen?",
    a: "Nej, priset är detsamma alla dagar. Herrklippning kostar 450 kr, skäggtrimning 380 kr, oavsett veckodag.",
  },
  {
    q: "Vilka tjänster finns tillgängliga på söndagar?",
    a: "Alla tjänster finns tillgängliga på söndagar: herrklippning, skin fade, taper fade, skäggtrimning, herr och skägg-paketet och barnklippning.",
  },
  {
    q: "Är det mycket väntetid på söndagar?",
    a: "Det varierar. Lördagar brukar vara travlaste dagen. Söndagar är ofta lite lugnare, särskilt på förmiddagen. Boka online för att slippa vänta.",
  },
  {
    q: "Hur bokar jag tid för söndagsklippning?",
    a: "Boka via BokaDirekt: välj salong, tjänst och tid. Det tar 30 sekunder och du väljer om du vill ha en specifik barberare eller nästa lediga tid.",
  },
];

function Page() {
  return (
    <SiteShell>
      {/* Hero */}
      <section className="container-page pt-10 md:pt-16 pb-8">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Barberare öppet på söndagar</span>
        </nav>
        <div className="max-w-3xl">
          <div className="eyebrow">Öppet alla dagar</div>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Barberare öppet på söndagar i Stockholm
          </h1>
           <p className="mt-5 text-lg text-foreground/85 max-w-prose font-medium">
             Tailor's Barber har öppet på söndagar i alla fyra salonger: <a href="/barberare-stockholm-ahlens-city/" className="underline">Åhléns City</a> 11:00 till 19:00, <a href="/barberare-stockholm-taby/" className="underline">Täby</a> Centrum 10:00 till 18:00, Arninge 10:00 till 18:00 och Åkersberga 10:00 till 18:00. Samma priser som vardagar, herrklippning {PRICE.herrklippning} kr.
           </p>
          <p className="mt-5 text-lg text-foreground/75 max-w-prose">
            Letar du efter barberare öppettider söndagar i Stockholm? Tailor's Barber har öppet på
            söndagar på alla fyra salonger. Perfekt om du vill kombinera klippningen med helgens
            shopping, eller om vardagarna är fullbokade. Boka online eller kom förbi på <a href="/drop-in-barber-stockholm/" className="underline">drop-in</a> i mån
            av tid.
          </p>
        </div>
      </section>

       <section className="container-page py-10">
         <h2 className="text-2xl md:text-3xl mb-5">Söndagsöppettider per salong</h2>
         <div className="card-elev overflow-hidden max-w-3xl">
           <table className="w-full text-left">
             <thead>
               <tr className="border-b border-border/60">
                 <th className="px-5 py-4 font-medium">Salong</th>
                 <th className="px-5 py-4 font-medium">Adress</th>
                 <th className="px-5 py-4 text-right font-medium">Söndag</th>
               </tr>
             </thead>
             <tbody>
               {SALONS.map((salon, index) => (
                 <tr key={salon.name} className={index > 0 ? "border-t border-border/60" : ""}>
                   <td className="px-5 py-4 font-medium">{salon.name}</td>
                   <td className="px-5 py-4">{salon.address}</td>
                   <td className="px-5 py-4 text-right whitespace-nowrap">{salon.sundayHours}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
       </section>

      {/* Salon grid */}
      <section className="container-page py-10 md:py-16">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow">Öppettider söndag</div>
          <h2 className="mt-3 text-3xl md:text-4xl">Fyra salonger, öppna på söndagar</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {SALONS.map((salon) => (
            <article key={salon.name} className="card-elev p-6 md:p-8 flex flex-col gap-4">
              <div>
                <div className="eyebrow">{salon.subtitle}</div>
                <h3 className="mt-1 text-2xl">{salon.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{salon.address}</p>
              </div>
              <div className="flex items-center gap-3 bg-muted/40 rounded-lg px-4 py-3">
                <span className="text-brass-deep font-semibold text-sm uppercase tracking-wide">Söndag</span>
                <span className="text-xl font-medium">{salon.sundayHours}</span>
              </div>
              <p className="text-sm text-foreground/70">{salon.note}</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                <a
                  href={withBookingUtm(salon.bokaDirektUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("book_now_click", { cta_location: `sunday_page_${salon.name}` })}
                  className="btn-brass text-center"
                >
                  Boka tid
                </a>
                <Link to={internalPath(salon.url)} className="btn-outline text-center">
                  Om salongen
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Why Sunday */}
      <section className="container-page py-12 md:py-20 border-t border-border">
        <div className="grid gap-10 md:grid-cols-2 items-start max-w-5xl">
          <div>
            <div className="eyebrow">Fördelar</div>
            <h2 className="mt-3 text-3xl md:text-4xl">Varför klippa sig på söndagen?</h2>
            <p className="mt-4 text-foreground/75">
              Söndagsklippning är ett smart val för den som inte hinner under veckans hektiska dagar.
            </p>
          </div>
          <div className="grid gap-5">
            {[
              {
                title: "Kombinera med helgens shopping",
                text: "Alla våra salonger ligger i stora handelscentrum. Passa på att klippa dig när du ändå är i Täby Centrum, Åhléns, Arninge eller ICA Maxi.",
              },
              {
                title: "Alla tjänster tillgängliga",
                text: "Herrklippning, skin fade, taper fade, skäggtrimning och barnklippning — hela utbudet finns på söndagar, samma priser som övriga dagar.",
              },
              {
                title: "Ofta bättre chans för drop-in",
                text: "Lördagar är vår travlaste dag. Söndagar är ofta lite lugnare — bättre chans att komma in utan lång väntetid, särskilt på förmiddagen.",
              },
              {
                title: "Boka online dygnet runt",
                text: "Boka din söndagstid via BokaDirekt — välj salong, barberare och tid på 30 sekunder. Du kan boka när som helst under veckan.",
              },
            ].map((item) => (
              <div key={item.title} className="flex gap-4">
                <span className="text-brass-deep mt-1 shrink-0">→</span>
                <div>
                  <h3 className="font-medium">{item.title}</h3>
                  <p className="mt-1 text-sm text-foreground/70">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="container-page py-12 border-t border-border">
        <div className="eyebrow mb-4">Tjänster</div>
        <h2 className="text-2xl md:text-3xl mb-6">Vad vill du boka på söndagen?</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link to="/herrklippning-stockholm/" className="card-elev p-5 hover:border-brass-deep transition-colors">
            <h3 className="font-medium">Herrklippning</h3>
            <p className="text-sm text-muted-foreground mt-1">Klassisk klippning med konsultation. Från 450 kr.</p>
          </Link>
          <Link to="/skin-fade/" className="card-elev p-5 hover:border-brass-deep transition-colors">
            <h3 className="font-medium">Skin Fade</h3>
            <p className="text-sm text-muted-foreground mt-1">Skarp kontrast med fade till huden. Från 450 kr.</p>
          </Link>
          <Link to="/taper-fade/" className="card-elev p-5 hover:border-brass-deep transition-colors">
            <h3 className="font-medium">Taper Fade</h3>
            <p className="text-sm text-muted-foreground mt-1">Mjuk övergång — tidlös och mångsidig. Från 450 kr.</p>
          </Link>
          <Link to="/skaggtrimning/" className="card-elev p-5 hover:border-brass-deep transition-colors">
            <h3 className="font-medium">Skäggtrimning</h3>
            <p className="text-sm text-muted-foreground mt-1">Formning, linjering och finish. 380 kr.</p>
          </Link>
          <Link to="/herrklippning-skaggtrimning/" className="card-elev p-5 hover:border-brass-deep transition-colors">
            <h3 className="font-medium">Klippning & Skägg</h3>
            <p className="text-sm text-muted-foreground mt-1">Hår och skägg i ett pass. Populäraste paketet.</p>
          </Link>
          <Link to="/barnklippning-stockholm/" className="card-elev p-5 hover:border-brass-deep transition-colors">
            <h3 className="font-medium">Barnklippning</h3>
            <p className="text-sm text-muted-foreground mt-1">Upp till 10 år. 380 kr.</p>
          </Link>
        </div>
      </section>

      <Faq items={FAQ_ITEMS} heading="Vanliga frågor om söndagsöppet" />
      <RelatedGuidesBlock slug={SLUG} />
    </SiteShell>
  );
}

export const Route = createFileRoute("/barberare-oppet-sondag-stockholm")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absUrl(CANONICAL) },
    ],
    links: [{ rel: "canonical", href: absUrl(CANONICAL) }],
    scripts: [
      jsonLd(faqSchema(FAQ_ITEMS)),
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Barberare öppet söndag Stockholm", url: `/${SLUG}/` },
        ]),
      ),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Tailor's Barber",
        url: SITE_URL,
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: "Sunday",
            opens: "10:00",
            closes: "18:00",
            name: "Täby Centrum, Arninge, Åkersberga",
          },
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: "Sunday",
            opens: "11:00",
            closes: "19:00",
            name: "Åhléns City Stockholm",
          },
        ],
      }),
    ],
  }),
  component: Page,
});
